import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { InputFieldComponent } from '../../../../../components/ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../../../../components/ui/customizable-button/customizable-button.component';
import { StepperControlsComponent } from '../../../../../components/ui/stepper-controls/stepper-controls.component';
import { ProgressCircleComponent } from '../../../../../components/progress-circle/progress-circle.component';
import { Router } from '@angular/router';
import {
  CsvServiceService,
  IBatchUploadProgress,
} from '../../../../../services/csv/csv-service.service';
import { cn, getCountryCode } from '../../../../../../utils/utils';

import {
  ReactiveFormsModule,
  Validators,
  FormBuilder,
  AbstractControl,
} from '@angular/forms';

import { renameKey } from '../../../../../../utils/utils';
import { IContact } from '../../../../../types/contactModule';
import { formatCount } from '../../../../../../utils/sms';

type MappingKey =
  | 'firstName'
  | 'lastName'
  | 'primaryContactNumber'
  | 'emailAddress'
  | 'gender'
  | 'birthDate'
  | 'country'
  | 'city';

interface MappingField {
  key: MappingKey;
  label: string;
  required?: boolean;
  hint: string;
  /** Header names that mean this field, compared without case, spaces or punctuation. */
  synonyms: string[];
}

const FIELDS: MappingField[] = [
  {
    key: 'firstName',
    label: 'First name',
    required: true,
    hint: 'Choose the column with first names',
    synonyms: ['firstname', 'first', 'givenname', 'forename', 'name', 'fullname', 'contactname'],
  },
  {
    key: 'lastName',
    label: 'Last name',
    hint: '',
    synonyms: ['lastname', 'last', 'surname', 'familyname'],
  },
  {
    key: 'primaryContactNumber',
    label: 'Phone number',
    required: true,
    hint: 'Choose the column with phone numbers',
    synonyms: [
      'primarycontactnumber',
      'phone',
      'phonenumber',
      'mobile',
      'mobilenumber',
      'mobileno',
      'msisdn',
      'cell',
      'cellphone',
      'cellnumber',
      'contactnumber',
      'telephone',
      'tel',
      'number',
      'whatsapp',
    ],
  },
  {
    key: 'emailAddress',
    label: 'Email',
    hint: '',
    synonyms: ['emailaddress', 'email', 'mail', 'emailid'],
  },
  { key: 'gender', label: 'Gender', hint: '', synonyms: ['gender', 'sex'] },
  {
    key: 'birthDate',
    label: 'Birth date',
    hint: '',
    synonyms: ['birthdate', 'dateofbirth', 'dob', 'birthday'],
  },
  { key: 'country', label: 'Country', hint: '', synonyms: ['country', 'nation', 'countryname'] },
  { key: 'city', label: 'City', hint: '', synonyms: ['city', 'town'] },
];

/**
 * "'+26771234567" → "+26771234567". Spreadsheets, and our own "rows to fix"
 * downloads, put an apostrophe before a leading +, =, - or @ to keep the cell
 * as text; it is not part of the value.
 */
function stripTextMarker(value: string): string {
  return /^'[=+\-@\t\r]/.test(value) ? value.slice(1) : value;
}

/** "E-mail Address" → "emailaddress". */
function normalizeHeader(value: string): string {
  return (value ?? '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
}

function convertGender(gender: string): string {
  const g = (gender ?? '').toString().trim().toUpperCase();
  if (g === 'MALE' || g === 'M') return 'MALE';
  if (g === 'FEMALE' || g === 'F') return 'FEMALE';
  return 'OTHER';
}

// Accepts: dd/mm/yyyy, dd-mm-yyyy, yyyy-mm-dd, yyyy/mm/dd, mm/dd/yyyy
// (ambiguous mm/dd vs dd/mm is resolved in favour of dd/mm — same as before),
// ISO datetime strings, and Excel serial date numbers (when xlsx returns them
// formatted as numeric strings). Returns an ISO string for a valid date, '' when
// no value was provided (birth date is optional), or null only when a non-empty
// value cannot be parsed.
function parseAndFormatDate(input: string | number | null | undefined): string | null {
  if (input === null || input === undefined) return '';
  const raw = String(input).trim();
  if (!raw) return '';

  // dd/mm/yyyy or dd-mm-yyyy (kept first to preserve original day-first behaviour)
  const dmy = raw.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
  if (dmy) {
    const [_, day, month, year] = dmy;
    const d = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    if (!isNaN(d.getTime())) return d.toISOString();
  }

  // yyyy-mm-dd or yyyy/mm/dd
  const ymd = raw.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/);
  if (ymd) {
    const [_, year, month, day] = ymd;
    const d = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    if (!isNaN(d.getTime())) return d.toISOString();
  }

  // Excel serial number (days since 1899-12-30)
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const serial = Number(raw);
    if (serial > 0 && serial < 100000) {
      const ms = (serial - 25569) * 86400 * 1000;
      const d = new Date(ms);
      if (!isNaN(d.getTime())) return d.toISOString();
    }
  }

  // ISO or any Date-parseable string as a last resort
  const fallback = new Date(raw);
  if (!isNaN(fallback.getTime())) return fallback.toISOString();

  return null;
}

@Component({
  selector: 'csv-column-mapping',
  standalone: true,
  imports: [
    InputFieldComponent,
    ReactiveFormsModule,
    StepperControlsComponent,
    ProgressCircleComponent,
  ],
  templateUrl: './column-mapping.component.html',
  styleUrls: ['../csv-step.css', './column-mapping.component.css'],
})
export class ColumnMappingComponent implements OnInit, OnDestroy {
  router = inject(Router);
  csvService = inject(CsvServiceService);
  builder = inject(FormBuilder);
  merge = cn;

  submitted: boolean = false;
  batchProgress: IBatchUploadProgress = {
    isBatching: false,
    currentBatch: 0,
    totalBatches: 0,
    percentage: 0,
  };
  private batchProgressSub?: Subscription;

  readonly fields = FIELDS;
  readonly formatCount = formatCount;

  showAutoMappingPrompt: boolean = false;
  closeAutoMappingPrompt() {
    this.showAutoMappingPrompt = false;
  }
  openAutoMappingPrompt() {
    this.showAutoMappingPrompt = true;
  }

  /** Fields matched from their header names, so "Undo" can clear just those. */
  autoMatched: MappingKey[] = [];

  get headers(): string[] {
    return this.csvService.getTableHeaders;
  }

  get rowCount(): number {
    return this.csvService.getTableData.length;
  }

  /** First value in this column within the first 20 rows, for the Sample column. */
  sampleFor(key: MappingKey): string {
    const header = (this.mapperForm.get(key)?.value ?? '') as string;
    if (!header) return '';
    const index = this.headers.indexOf(header);
    if (index < 0) return '';
    for (const row of this.csvService.getTableData.slice(0, 20)) {
      const value = stripTextMarker((row?.[index] ?? '').toString().trim());
      if (value) return value.length > 48 ? value.slice(0, 47) + '…' : value;
    }
    return '';
  }

  /** Another field already reading this column, if any. */
  sharedWith(key: MappingKey): string {
    const header = this.mapperForm.get(key)?.value;
    if (!header) return '';
    const other = FIELDS.find((field) => field.key !== key && this.mapperForm.get(field.key)?.value === header);
    return other ? other.label : '';
  }

  isMissing(field: MappingField): boolean {
    return !!field.required && this.submitted && !this.mapperForm.get(field.key)?.value;
  }

  get mappedCount(): number {
    return FIELDS.filter((field) => !!this.mapperForm.get(field.key)?.value).length;
  }

  /** Matches columns whose names mean a contact field (Mobile, MSISDN, Surname…). */
  private autoMatch() {
    const used = new Set<string>();
    const normalized = this.headers.map((header) => normalizeHeader(header));
    const matched: MappingKey[] = [];
    // Exact field names first (the template), then the other synonyms.
    for (const pass of [0, 1]) {
      for (const field of FIELDS) {
        if (this.mapperForm.get(field.key)?.value) continue;
        const wanted = pass === 0 ? [normalizeHeader(field.key)] : field.synonyms;
        const index = normalized.findIndex((header, i) => !used.has(this.headers[i]) && wanted.includes(header));
        if (index < 0) continue;
        used.add(this.headers[index]);
        this.mapperForm.get(field.key)?.setValue(this.headers[index]);
        matched.push(field.key);
      }
    }
    this.autoMatched = matched;
    this.showAutoMappingPrompt = matched.length > 0;
  }

  undoAutoMatch() {
    for (const key of this.autoMatched) this.mapperForm.get(key)?.setValue('');
    this.autoMatched = [];
    this.closeAutoMappingPrompt();
  }

  // this is the form controls for the mapping of the forms
  mapperForm = this.builder.group({
    firstName: ['', Validators.required],
    lastName: [''],
    emailAddress: [''],
    primaryContactNumber: ['', Validators.required],
    gender: [''],
    birthDate: [''],
    country: [''],
    city: [''],
  });

  // mappedForm = [
  //   ['firstName', this.mapperForm.value.firstName],
  //   ['lastName', ''],
  //   ['emailAddress', ''],
  //   ['primaryContactNumber', ''],
  //   ['gender', ''],
  //   ['birthDate', ''],
  //   ['country', ''],
  //   ['city', ''],
  // ];
  get getForm() {
    return this.mapperForm.value;
  }

  goToPrevious() {
    this.router.navigate(['organization', 'contacts', 'csv']);
  }

  // this function checks and see if the user is using black communications template we provided to them
  get getUsingTemplate() {
    const requiredElements = [
      'firstName',
      'lastName',
      'emailAddress',
      'primaryContactNumber',
      'gender',
      'birthDate',
      'country',
    ];
    return requiredElements.every((element: string) =>
      this.csvService.getTableHeaders.includes(element)
    );
  }

  handleAutoMapping() {
    this.mapperForm.patchValue({
      firstName: 'firstName',
      lastName: 'lastName',
      emailAddress: 'emailAddress',
      primaryContactNumber: 'primaryContactNumber',
      gender: 'gender',
      birthDate: 'birthDate',
      country: 'country',
      city: 'city',
    });
    this.closeAutoMappingPrompt();
  }

  ngOnInit() {
    this.autoMatch();
    this.batchProgressSub = this.csvService.batchUploadProgress$.subscribe(
      (progress) => (this.batchProgress = progress)
    );
  }

  ngOnDestroy() {
    this.batchProgressSub?.unsubscribe();
  }

  goToReview() {
    this.submitted = true;
    if (!this.mapperForm.valid) return;

    // Mapping + validation over the full dataset is heavy for large files. Flip the
    // loading flag (the stepper button reflects it) and defer the work off the click
    // stack so the loading state paints before the main thread gets busy.
    this.csvService.isLoading = true;
    setTimeout(() => this.runMappingAndValidate(), 0);
  }

  private runMappingAndValidate() {
    // The mapped rows are released after each check; coming back to remap
    // rebuilds them from the parsed file.
    if (!this.csvService.getObjectifiedData.length && this.csvService.getTableData.length) {
      this.csvService.generateObjectifiedData(this.csvService.getTableHeaders, this.csvService.getTableData);
    }
    const objectifiedData = this.csvService.getObjectifiedData;
    const mapping = this.mapperForm.value as Record<string, string | null | undefined>;
    const formKeys = Object.keys(mapping);

    // Single pass over rows. No source mutation, no nested loop over rows.
    const finalResult = objectifiedData.map((item: Record<string, any>) => {
      const mapped: Record<string, any> = {};
      for (const key of formKeys) {
        // Preserve original quirk: only fall back to `key` when the mapping is
        // null/undefined. An empty-string mapping means "left blank" and yields ''.
        const sourceKey = mapping[key] ?? key;
        const value = item[sourceKey] ?? '';
        mapped[key] = typeof value === 'string' ? stripTextMarker(value) : value;
      }
      mapped['gender'] = convertGender(`${mapped['gender']}`);
      mapped['birthDate'] = parseAndFormatDate(String(mapped['birthDate']));

      return {
        firstName: '',
        lastName: '',
        nickName: '',
        zipCode: '',
        city: '',
        country: '',
        birthDate: '',
        countryCode: getCountryCode(mapped['country'])?.countryCode,
        gender: '',
        primaryContactNumber: '',
        emailAddress: '',
        customerStage: 'ACTIVE',
        type: 'CONTACT',
        recordStatus: 'ACTIVE',
        contactDetails: [],
        customAttributes: [],
        notes: [],
        entityTags: [],
        companyEntities: [],
        ...mapped,
      };
    });

    this.csvService.updateObjectifiedData(
      finalResult as (IContact & Record<string, any>)[]
    );
    this.csvService
      .applyMappedContactsAsValidated(
        finalResult as (IContact & Record<string, any>)[]
      )
      .then(() => {
        this.csvService.isLoading = false;
        this.router.navigate(['organization', 'contacts', 'csv'], {
          queryParams: { title: 'cleanup' },
        });
      });
  }
}
